import { jointGap } from './jointMoves';
import { faceNormal } from './partFaces';
import type { PartItem } from './partsRun';
import { fastenerKind, holeOnUnit, type StepHole } from './stepHoles';
import type { Vec3 } from './assemblyPose';

// HOW A JOINT COMES APART, EXPLODED: its parts slid apart ALONG ITS HARDWARE, where the whole product
// spreads every part out from its middle (see explodeShifts).
//
// Out from the middle is the product's way, and over a joint it opens the wrong gap: a rail and the
// frame its dowels go into stayed where they were, and the dowels, backed out of the rail, stood
// inside the frame — nothing of them to see. A joint's hardware has a line, and that line is where
// the gap has to be. So each line pushes the part it goes on into away from the part it enters, along
// the line, until the two stand jointGap apart: the hardware, at the start of its movement, stands
// between them and in neither.
//
// A PART PUSHED ALREADY IS LEFT WHERE IT IS, and the other one moves: a frame that two rails go into
// from opposite sides cannot be pushed both ways, so it is pushed by the first and the second rail
// steps back instead.

type Unit = { id: string; hand: 'left' | 'right' | null; mirror: boolean };
type Point = { x: number; y: number; z: number };

const dot = (a: Vec3, b: Vec3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

// EACH UNIT'S SHIFT from where the product has it, at fully apart, in the order of `units`.
// `lenOf`: a line's hardware's length, off its model — null where it is not known yet, and that line
// then opens no gap.
export function jointSpread(
  units: Unit[],
  holes: StepHole[],
  items: PartItem[],
  lenOf: (entry: StepHole) => number | null,
): Point[] {
  const at: Vec3[] = units.map(() => [0, 0, 0]);
  const unitOf = (h: StepHole) => units.findIndex((u) => holeOnUnit(h, u.id, u.hand));
  // ONE PUSH PER PAIR OF UNITS AND DIRECTION — four dowels between a rail and a frame are one gap.
  const pushes = new Map<string, { from: number; to: number; d: Vec3; gap: number }>();
  for (const entry of holes) {
    if (entry.via == null || (entry.line != null && entry.line !== entry.id)) continue;
    const kind = fastenerKind(items[Number(entry.via)]);
    const len = lenOf(entry);
    const from = unitOf(entry);
    if (!kind || len == null || from < 0) continue;
    // Its face's outward normal, on the unit it is on: the reflected unit of a pair has it across x.
    const n = faceNormal(entry.face);
    const out: Vec3 = units[from].mirror ? [-n[0], n[1], n[2]] : n;
    // WHERE THE NEXT PART LIES: out of the face for a dowel, half of which stands out of it; through
    // the part for a screw.
    const d: Vec3 = kind === 'seated' ? out : [-out[0], -out[1], -out[2]];
    for (const next of holes) {
      if (next === entry || next.line !== entry.id || next.via !== entry.via || next.part === entry.part) continue;
      const to = unitOf(next);
      if (to < 0 || to === from) continue;
      const key = `${from}|${to}|${d.map(Math.round).join(',')}`;
      const gap = jointGap(len);
      if ((pushes.get(key)?.gap ?? -1) < gap) pushes.set(key, { from, to, d, gap });
    }
  }
  const moved = new Set<number>();
  for (const { from, to, d, gap } of pushes.values()) {
    const apart = dot([at[to][0] - at[from][0], at[to][1] - at[from][1], at[to][2] - at[from][2]], d);
    if (apart >= gap) continue;
    const step = gap - apart;
    const [k, sign] = moved.has(to) && !moved.has(from) ? [from, -1] : [to, 1];
    at[k] = [at[k][0] + sign * step * d[0], at[k][1] + sign * step * d[1], at[k][2] + sign * step * d[2]];
    moved.add(k);
  }
  return at.map(([x, y, z]) => ({ x, y, z }));
}
